import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useChatbots } from "../contexts/ChatbotContext";
import Layout from "../components/Layout";
import DirectoryTree from "../components/DirectoryTree";
import AccessControl from "../components/AccessControl";
import FunctionSelector from "../components/FunctionSelector";
import config from "../config";
import "./EditToolPage.css"; // Using the new CSS file
import downCaret from "../public/img/down_caret.svg";

const EditToolPage = () => {
  const navigate = useNavigate();
  const { toolId } = useParams();
  const { getAllChatbots, refreshChatbots } = useChatbots();

  const [isLoading, setIsLoading] = useState(true); // True initially for fetching tool data
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [availableModels, setAvailableModels] = useState([]);
  const [showDirectoryTree, setShowDirectoryTree] = useState(false);
  const [originalToolData, setOriginalToolData] = useState(null); // To compare for changes or reset
  const directorySelectorRef = useRef(null);
  const [initialTemperature, setInitialTemperature] = useState(1.0); // Track initial temperature

  // Form fields
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    directory: false, // Always false for editing a tool
    parent: "",
    icon: "",
    logo: "", // Added logo field
    tags: [],
    access: ["All"], // Default access
    provider: "openai",
    model: "",
    temperature: 1.0,
    prompt: "",
    functions: [],
    starter_questions: [],
    folder: "",
  });

  // Get all chatbots for directory tree
  const allChatbotsForTree = getAllChatbots ? getAllChatbots() : [];

  // Get the selected parent directory name
  const getSelectedDirectoryName = () => {
    if (!formData.parent) {
      return "Root (No Parent)";
    }
    const selectedDirectory = allChatbotsForTree.find(
      (chatbot) => chatbot._id === formData.parent
    );
    return selectedDirectory
      ? selectedDirectory.title
      : "Select a parent directory";
  };

  // Tag input
  const [tagInput, setTagInput] = useState("");

  // Starter question input
  const [questionInput, setQuestionInput] = useState("");


  // Fetch available models
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await fetch(
          `${config.api.baseUrl}${config.api.endpoints.dashboard.models}`,
          { credentials: "include" }
        );
        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }
        const data = await response.json();
        if (data.success) {
          setAvailableModels(data.data || []);
          // Default model will be set after fetching tool data if tool has a model
        } else {
          setErrorMessage(data.error || "Failed to load available models.");
        }
      } catch (error) {
        console.error("Failed to fetch models:", error);
        setErrorMessage(
          `Failed to load available models: ${error.message}. Please try again later.`
        );
      }
    };
    fetchModels();
  }, []);

  // Fetch existing tool data
  useEffect(() => {
    if (!toolId) {
      setErrorMessage("No Tool ID provided.");
      setIsLoading(false);
      return;
    }
    const fetchToolData = async () => {
      setIsLoading(true);
      setErrorMessage("");
      try {
        const response = await fetch(
          `${config.api.baseUrl}/chatbot/${toolId}`,
          {
            credentials: "include",
          }
        );
        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw new Error(
            errorData?.error ||
              `Error ${response.status}: ${response.statusText}`
          );
        }
        const data = await response.json();
        if (data.success && data.chatbot) {
          const tool = data.chatbot;
          const driveUrl = tool.folder
            ? `https://drive.google.com/drive/folders/${tool.folder}`
            : "";

          const toolTemperature = tool.temperature !== undefined ? tool.temperature : 1.0;
          setInitialTemperature(toolTemperature); // Save initial temperature for reset
          
          setFormData({
            title: tool.title || "",
            description: tool.description || "",
            directory: false, // Explicitly false
            parent: tool.parent || "", // Ensure parent is string or empty string
            icon: tool.icon || "",
            logo: tool.logo || "",
            tags: tool.tags || [],
            access:
              tool.access && tool.access.length > 0 ? tool.access : ["All"],
            provider: tool.provider || "openai",
            model: tool.model || "",
            temperature: toolTemperature,
            prompt: tool.prompt || "",
            functions: tool.functions || [],
            starter_questions: tool.starter_questions || [],
            folder: driveUrl,
          });
          setOriginalToolData(tool); // Save original data
          // Missing or deleted model: preselect the top-ranked model of the
          // tool's provider (availableModels is rank-sorted) instead of
          // resubmitting a stale id.
          const modelExists =
            tool.model && availableModels.some((m) => m.id === tool.model);
          if (!modelExists && availableModels.length > 0) {
            const providerFirst = availableModels.find(
              (m) =>
                m.provider === (tool.provider || "openai") &&
                (m.public === undefined || m.public === true)
            );
            if (providerFirst) {
              setFormData((prev) => ({ ...prev, model: providerFirst.id }));
            }
          }
        } else {
          setErrorMessage(data.error || "Failed to fetch tool data.");
        }
      } catch (error) {
        console.error("Failed to fetch tool data:", error);
        setErrorMessage(
          `Failed to load tool data: ${error.message}. Please try again later.`
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchToolData();
  }, [toolId, availableModels]); // Rerun if availableModels changes for default model logic

  // Handle GPT-5 models - set temperature to 1 and disable field (except GPT-5.2)
  useEffect(() => {
    const modelLower = formData.model ? formData.model.toLowerCase() : '';
    const isGPT5Model = modelLower && (
      (modelLower.includes('gpt-5') && !modelLower.includes('gpt-5.2')) ||
      modelLower === 'o1' ||
      modelLower.includes('o1-')
    );
    
    if (isGPT5Model) {
      setFormData((prev) => ({
        ...prev,
        temperature: 1.0,
      }));
    }
  }, [formData.model]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === "checkbox" ? checked : value;

    if (name === "temperature") {
      setFormData((prev) => ({ ...prev, [name]: parseFloat(value) }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: val }));
    }
  };

  // Handle directory selection (single click - don't close)
  const handleDirectorySelect = (directoryId) => {
    setFormData((prev) => ({ ...prev, parent: directoryId }));
    // Don't close the tree on single click
  };

  // Close directory tree
  const handleCloseDirectoryTree = () => {
    setShowDirectoryTree(false);
  };

  const toggleDirectoryTree = () => setShowDirectoryTree((prev) => !prev);
  
  // Handle click outside to close directory tree
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (directorySelectorRef.current && !directorySelectorRef.current.contains(event.target)) {
        setShowDirectoryTree(false);
      }
    };

    if (showDirectoryTree) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDirectoryTree]);
  
  const handleAccessChange = useCallback((accessList) => {
    setFormData((prev) => ({ ...prev, access: accessList }));
  }, []);

  const handleAddTag = () => {
    if (tagInput.trim()) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput("");
    }
  };
  const handleRemoveTag = (index) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index),
    }));
  };

  const handleAddQuestion = () => {
    if (questionInput.trim()) {
      setFormData((prev) => ({
        ...prev,
        starter_questions: [...prev.starter_questions, questionInput.trim()],
      }));
      setQuestionInput("");
    }
  };
  const handleRemoveQuestion = (index) => {
    setFormData((prev) => ({
      ...prev,
      starter_questions: prev.starter_questions.filter((_, i) => i !== index),
    }));
  };

  const handleFunctionChange = (selectedFunctions) => {
    setFormData((prev) => ({ ...prev, functions: selectedFunctions }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title) {
      setErrorMessage("Title is required.");
      return;
    }
    setIsSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");

    // Prepare payload, ensuring numeric fields are numbers
    const payload = { ...formData };

    if (payload.folder) {
      try {
        const u = new URL(payload.folder.trim());
        const parts = u.pathname.split("/").filter(Boolean);
        const idx = parts.indexOf("folders");
        if (idx !== -1 && parts[idx + 1]) {
          payload.folder = parts[idx + 1];
        } else {
          const qp = u.searchParams.get("id");
          if (qp) payload.folder = qp;
        }
      } catch (e) {
        if (payload.folder.includes("/folders/")) {
          const segs = payload.folder.split("/");
          payload.folder = segs[segs.length - 1];
        }
      }
    } else if (!payload.folder) {
      payload.folder = "";
    }

    payload.temperature = parseFloat(formData.temperature);
    payload.parent = formData.parent || null;
    // Remove 'directory' as it's not part of the chatbot schema for updates in this context.
    // The backend PUT route also removes immutable fields.
    delete payload.directory;

    try {
      const response = await fetch(`${config.api.baseUrl}/chatbot/${toolId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include",
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setSuccessMessage("Tool updated successfully!");
        if (data.chatbot) {
          // Update formData with potentially modified data from backend (e.g. updated_at)
          const tool = data.chatbot;
          const driveUrl = tool.folder
            ? `https://drive.google.com/drive/folders/${tool.folder}`
            : "";
          setFormData({
            title: tool.title || "",
            description: tool.description || "",
            directory: false,
            parent: tool.parent || "",
            icon: tool.icon || "",
            logo: tool.logo || "",
            tags: tool.tags || [],
            access:
              tool.access && tool.access.length > 0 ? tool.access : ["All"],
            provider: tool.provider || "openai",
            model: tool.model || "",
            temperature:
              tool.temperature !== undefined ? tool.temperature : 1.0,
            prompt: tool.prompt || "",
            functions: tool.functions || [],
            starter_questions: tool.starter_questions || [],
            folder: driveUrl,
          });
          setOriginalToolData(tool);
        }
        refreshChatbots(); // Refresh context if needed
        // navigate("/my-tools"); // Optionally navigate away
      } else {
        setErrorMessage(data.error || "Failed to update tool.");
      }
    } catch (error) {
      console.error("Failed to update tool:", error);
      setErrorMessage(`An error occurred: ${error.message}. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete chatbot
  const handleDelete = async () => {
    if (!toolId) return;
    const confirmed = window.confirm(
      "Are you sure you want to permanently delete this chatbot? This cannot be undone."
    );
    if (!confirmed) return;
    setIsSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const resp = await fetch(`${config.api.baseUrl}/chatbot/${toolId}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.success) {
        setSuccessMessage("Chatbot deleted.");
        refreshChatbots && refreshChatbots();
        navigate("/my-tools");
      } else {
        setErrorMessage(data.error || "Failed to delete chatbot.");
      }
    } catch (e) {
      setErrorMessage("Network error while deleting chatbot.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e, action) => {
    if (e.key === "Enter") {
      e.preventDefault();
      action();
    }
  };

  const isSvgContent = (str) =>
    typeof str === "string" && str.trim().startsWith("<svg");

  if (isLoading) {
    return (
      <Layout>
        <div className="edit-page">
          <div className="edit-container">
            <div className="loading-spinner-container">
              Loading tool details...
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="edit-page">
        <div className="edit-container">
          <h1 className="edit-title">
            Edit Tool: {originalToolData?.title || "Loading..."}
          </h1>

          {errorMessage && <div className="error-message">{errorMessage}</div>}
          {successMessage && (
            <div className="success-message">{successMessage}</div>
          )}

          <form onSubmit={handleSubmit} className="edit-form">
            {/* General Information Section */}
            <div className="form-section">
              <h2>General Information</h2>
              <div className="form-section-columns">
                <div className="form-group">
                  <label htmlFor="title">
                    Title <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="form-group full-width">
                <label>Parent Directory</label>
                <div className="directory-selector" ref={directorySelectorRef}>
                  <div
                    className="selected-directory"
                    onClick={toggleDirectoryTree}
                  >
                    <span>{getSelectedDirectoryName()}</span>
                    <span className="directory-selector-toggle">
                      <img
                        src={downCaret}
                        alt="toggle"
                        className={`dir-caret ${
                          showDirectoryTree ? "open" : ""
                        }`}
                      />
                    </span>
                  </div>
                  {showDirectoryTree && (
                    <div className="directory-tree-wrapper" onClick={(e) => e.stopPropagation()}>
                      <DirectoryTree
                        chatbots={allChatbotsForTree.filter(
                          (cb) => cb.directory
                        )}
                        onSelectDirectory={handleDirectorySelect}
                        onClose={handleCloseDirectoryTree}
                        currentParentId={formData.parent}
                        currentChatbotId={toolId} // Prevent selecting itself as parent
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Display & Branding Section */}
            <div className="form-section">
              <h2>Display & Branding</h2>
              <div className="form-section-columns">
                <div className="form-group">
                  <label htmlFor="icon">Icon (SVG code)</label>
                  <textarea
                    id="icon"
                    name="icon"
                    value={formData.icon}
                    onChange={handleInputChange}
                    placeholder="<svg>...</svg>"
                    rows={3}
                  />
                  <small>Paste SVG code directly.</small>
                </div>
                <div className="form-group">
                  <label htmlFor="logo">Logo (SVG code, for chat window)</label>
                  <textarea
                    id="logo"
                    name="logo"
                    value={formData.logo}
                    onChange={handleInputChange}
                    placeholder="<svg>...</svg>"
                    rows={3}
                  />
                  <small>
                    Paste SVG code directly. This logo will be displayed at the
                    top of the chat window.
                  </small>
                </div>
              </div>
              <div className="form-group full-width">
                <label>Previews</label>
                <div className="icon-logo-preview">
                  {formData.icon && (
                    <div className="preview-box">
                      {isSvgContent(formData.icon) ? (
                        <div
                          className="svg-preview-container"
                          dangerouslySetInnerHTML={{ __html: formData.icon }}
                        />
                      ) : (
                        <img src={formData.icon} alt="Icon Preview" />
                      )}
                    </div>
                  )}
                  {formData.logo && (
                    <div className="preview-box">
                      {isSvgContent(formData.logo) ? (
                        <div
                          className="svg-preview-container"
                          dangerouslySetInnerHTML={{ __html: formData.logo }}
                        />
                      ) : (
                        <img src={formData.logo} alt="Logo Preview" />
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="form-group full-width">
                <label htmlFor="tags">Tags</label>
                <div className="tag-input-container">
                  <input
                    type="text"
                    id="tags"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, handleAddTag)}
                    placeholder="Add a tag and press Enter"
                  />
                  <button
                    type="button"
                    className="add-button"
                    onClick={handleAddTag}
                  >
                    Add
                  </button>
                </div>
                <div className="tags-container">
                  {formData.tags.map((tag, index) => (
                    <div key={index} className="tag">
                      {tag}
                      <button
                        type="button"
                        className="remove-tag"
                        onClick={() => handleRemoveTag(index)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* AI Configuration Section */}
            <div className="form-section">
              <h2>AI Configuration</h2>

              <div className="form-section-columns">
                <div className="form-group">
                  <label htmlFor="provider">Provider</label>
                  <div className="custom-select-wrapper">
                    <select
                      id="provider"
                      name="provider"
                      value={formData.provider}
                      onChange={handleInputChange}
                      className="custom-select"
                    >
                      <option value="openai">OpenAI</option>
                      <option value="google">Google</option>
                      <option value="anthropic">Anthropic</option>
                      <option value="perplexity">Perplexity</option>
                    </select>
                    <img
                      src={downCaret}
                      alt=""
                      className="custom-select-caret"
                      aria-hidden="true"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="model">Model</label>
                  <div className="custom-select-wrapper">
                    <select
                      id="model"
                      name="model"
                      value={formData.model}
                      onChange={handleInputChange}
                      className="custom-select"
                    >
                      <option value="">-- Select Model --</option>
                      {(availableModels || [])
                        .filter(
                          (m) =>
                            m.provider === formData.provider &&
                            (m.public === undefined || m.public === true)
                        )
                        .map((modelOption) => (
                          <option key={modelOption._id} value={modelOption.id}>
                            {modelOption.name} ({modelOption.id})
                          </option>
                        ))}
                    </select>
                    <img
                      src={downCaret}
                      alt=""
                      className="custom-select-caret"
                      aria-hidden="true"
                    />
                  </div>
                  {availableModels.filter(
                    (m) => m.provider === formData.provider
                  ).length === 0 &&
                    formData.provider && (
                      <small style={{ color: "orange" }}>
                        No models found for selected provider. Please add models
                        in Admin page.
                      </small>
                    )}
                </div>
                <div className="form-group">
                  <div className="range-label-container">
                    <label htmlFor="temperature">Temperature *</label>
                    {formData.temperature !== initialTemperature && (
                      <button
                        type="button"
                        className="range-reset-btn"
                        onClick={() => setFormData(prev => ({ ...prev, temperature: initialTemperature }))}
                        title={`Reset to initial (${initialTemperature.toFixed(2)})`}
                      >
                        Reset
                      </button>
                    )}
                  </div>
                  <div className="range-input-container">
                    <input
                      type="range"
                      id="temperature"
                      name="temperature"
                      min="0"
                      max="2"
                      step="0.01"
                      value={formData.temperature}
                      onChange={handleInputChange}
                      disabled={
                        formData.model && (
                          (formData.model.toLowerCase().includes('gpt-5') && !formData.model.toLowerCase().includes('gpt-5.2')) ||
                          formData.model.toLowerCase() === 'o1' ||
                          formData.model.toLowerCase().includes('o1-')
                        )
                      }
                      style={{
                        background: `linear-gradient(to right, var(--edit-primary) 0%, var(--edit-primary) ${(formData.temperature / 2) * 100}%, var(--edit-border-light) ${(formData.temperature / 2) * 100}%, var(--edit-border-light) 100%)`
                      }}
                    />
                    <span className="range-value">
                      {formData.temperature.toFixed(2)}
                    </span>
                  </div>
                  {formData.model && (
                    (formData.model.toLowerCase().includes('gpt-5') && !formData.model.toLowerCase().includes('gpt-5.2')) ||
                    formData.model.toLowerCase() === 'o1' ||
                    formData.model.toLowerCase().includes('o1-')
                  ) && (
                    <span className="field-note">
                      Temperature is fixed at 1.0 for this model
                    </span>
                  )}
                </div>
              </div>
              <div className="form-group full-width">
                <label htmlFor="prompt">System Prompt (Instructions)</label>
                <textarea
                  id="prompt"
                  name="prompt"
                  value={formData.prompt}
                  onChange={handleInputChange}
                  rows={6}
                />
              </div>

            </div>

            {/* Features Section */}
            <div className="form-section">
              <h2>Features</h2>
              <div className="form-group full-width">
                <label htmlFor="functions">Functions (Tools)</label>
                <FunctionSelector
                  selectedFunctions={formData.functions}
                  onChange={handleFunctionChange}
                />
              </div>
              <div className="form-group full-width">
                <label htmlFor="starter_questions">Starter Questions</label>
                <div className="tag-input-container">
                  {" "}
                  {/* Reusing tag styles for input group */}
                  <input
                    type="text"
                    id="starter_questions"
                    value={questionInput}
                    onChange={(e) => setQuestionInput(e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, handleAddQuestion)}
                    placeholder="Add a starter question and press Enter"
                  />
                  <button
                    type="button"
                    className="add-button"
                    onClick={handleAddQuestion}
                  >
                    Add
                  </button>
                </div>
                <div className="questions-container">
                  {formData.starter_questions.map((question, index) => (
                    <div key={index} className="question-item">
                      {question}
                      <button
                        type="button"
                        className="remove-question"
                        onClick={() => handleRemoveQuestion(index)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Access Control Section */}
            <div className="form-section">
              <h2>Access Control</h2>
              <div className="form-group full-width">
                <AccessControl
                  value={formData.access}
                  onChange={handleAccessChange}
                />
              </div>
            </div>

            {/* Google Drive Folder Section */}
            <div className="form-section">
              <h2>Google Drive Folder</h2>
              <div className="form-group full-width">
                <label htmlFor="folder">Google Drive Folder URL</label>
                <input
                  type="text"
                  id="folder"
                  name="folder"
                  value={formData.folder}
                  onChange={handleInputChange}
                  placeholder="Paste a Google Drive folder URL to sync files"
                />
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="cancel-button"
                onClick={() => navigate("/my-tools")}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="submit-button"
                disabled={isSubmitting || isLoading}
              >
                {isSubmitting ? "Saving..." : "Save Changes"}
              </button>
              <button
                type="button"
                className="delete-button"
                onClick={handleDelete}
                disabled={isSubmitting}
              >
                Delete Chatbot
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default EditToolPage;
